// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import { NumberField } from './NumberField';
import { FinlandMap } from './FinlandMap';
import { RegionSelector } from './RegionSelector';
import type { VaccineInputs } from '../lib/models';

interface RegionalCoverageCardProps {
  selectedRegions: string[];
  selectedRegionId: string | null;
  selectedRegionName: string;
  selectedRegionCoverage: number;
  coverageByRegion: VaccineInputs['coveragePctByRegion'];
  nationalCoveragePct: number;
  defaultCoveragePct: number;
  onCoverageChange: (value: number) => void;
  onRegionToggle: (regionKey: string) => void;
  onRegionsChange: (regions: string[]) => void;
  onDefaultCoverageChange: (value: number) => void;
}

export const RegionalCoverageCard: React.FC<RegionalCoverageCardProps> = ({
  selectedRegions,
  selectedRegionId,
  selectedRegionName,
  selectedRegionCoverage,
  coverageByRegion,
  nationalCoveragePct,
  defaultCoveragePct,
  onCoverageChange,
  onRegionToggle,
  onRegionsChange,
  onDefaultCoverageChange,
}) => {
  const hasSelections = selectedRegions.length > 0;
  const isFinlandSelected = selectedRegionId === 'finland';

  return (
    <div className="vaccine-demo__card">
      <div className="vaccine-demo__card-content">
        <h2 className="vaccine-demo__card-title">
          <svg className="vaccine-demo__card-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Regional Coverage
        </h2>

        <FinlandMap
          selectedRegions={selectedRegions}
          onRegionToggle={onRegionToggle}
          coverageData={coverageByRegion}
          aria-label="Interactive Finland map for regional coverage selection"
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
          <div>
            <div style={{ marginBottom: '4px' }}>
              <div className="vaccine-demo__region-selector-inline">
                <RegionSelector
                  selectedRegions={selectedRegions}
                  onRegionsChange={onRegionsChange}
                  aria-label="Select regions for coverage configuration"
                />
              </div>
            </div>
            <div style={{ fontSize: '16px', fontWeight: '500', color: '#f1f5f9' }}>
              {selectedRegions.length} region{selectedRegions.length !== 1 ? 's' : ''} selected
            </div>
          </div>
          {!selectedRegions.includes('finland') && !isFinlandSelected && selectedRegions.length > 0 && (
            <NumberField
              label={selectedRegions.length === 1 ? `Coverage in ${selectedRegionName} (%)` : `Coverage for all selected regions (%)`}
              value={selectedRegionCoverage}
              onChange={onCoverageChange}
              min={0}
              max={nationalCoveragePct}
              step={0.1}
            />
          )}
        </div>

        {!hasSelections && (
          <div style={{
            fontSize: '14px',
            color: '#94a3b8',
            fontStyle: 'italic',
            textAlign: 'center',
            marginTop: '16px',
            padding: '16px',
            backgroundColor: 'rgba(51, 65, 85, 0.3)',
            borderRadius: '8px',
            border: '1px solid rgba(71, 85, 105, 0.3)'
          }}>
            <div style={{ marginBottom: '8px' }}>🗺️ Click on regions in the map above to select them</div>
            <div>Selected regions will be highlighted and you can configure individual coverage rates</div>
          </div>
        )}

        {hasSelections && (
          <div style={{ marginTop: '24px' }}>
            {isFinlandSelected ? (
              <div style={{
                fontSize: '14px',
                color: '#cbd5f5',
                backgroundColor: 'rgba(51, 65, 85, 0.3)',
                borderRadius: '8px',
                padding: '12px',
                border: '1px solid rgba(71, 85, 105, 0.3)'
              }}>
                Finland (entire country) is selected. Coverage is governed by the national coverage cap.
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};
